import {createAsyncThunk, createSelector, createSlice} from '@reduxjs/toolkit';
import {checkUserPermission} from '../apis/admin/shared'
import {UserContextProps, WebContextProps} from "@/redux/slices/types";
import {SLUGGED_PROPS} from "@/components/layout/types";
import {GENERAL_OBJECT} from "@/components/types";

export interface Layout {
    isHeader: boolean;
    isSideBar: boolean;
    isFooter: boolean;
    isBreadcrumb: boolean
}

const name = "SETTINGS";

interface INITIAL_STATE {
    dir: string;
    locale: string;
    theme: number;
    fontSize: number,
    sidebarOpen: boolean,
    webContext: WebContextProps | null;
    userContext: UserContextProps | null
    globalLoading: boolean;
    layout: Layout;
    sidebarItems: SLUGGED_PROPS[];
    siteImages: {images: GENERAL_OBJECT, dir: null} | null
}

const initialState: INITIAL_STATE = {
    dir: "ltr",
    locale: "en",
    theme: 1,
    fontSize: 14,
    sidebarOpen: false,
    globalLoading: true,
    layout: {
        isHeader: false,
        isSideBar: false,
        isFooter: false,
        isBreadcrumb: false
    },
    webContext: null,
    userContext: null,
    sidebarItems: [],
    siteImages: null
}

export const settingsActions = {
    checkIfUserHasPermission: createAsyncThunk(`${name}/getUserContext`, async (data) => {
        return await checkUserPermission(data)
    }),
}

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        updateAppDirection: (state, {payload}) => {
            state.dir = payload;
        },
        updateAppTheme: (state, {payload}) => {
            state.theme = payload;
        },
        updateFontSize: (state, {payload}) => {
            state.fontSize = payload;
        },
        updateSidebarStatus: (state, {payload}) => {
            state.sidebarOpen = !state.sidebarOpen;
        },
        updateAppLanguage: (state, {payload}) => {
            state.locale = payload;
        },
        updateWebContext: (state, {payload}) => {
            state.webContext = payload
        },
        updateUserContext: (state, {payload}) => {
            state.userContext = payload
        },
        updateLayout: (state, {payload}: { payload: Layout }) => {
            state.layout = payload
        },
        updateGlobalLoading: (state, {payload}: { payload: boolean }) => {
            state.globalLoading = payload
        },
        updateSidebarItems: (state, {payload}: { payload: SLUGGED_PROPS[] }) => {
            state.sidebarItems = payload
        },
        updateSiteImages: (state, {payload}: { payload: {images: GENERAL_OBJECT, dir: null} }) => {
            state.siteImages = payload
        }
    },
    extraReducers: (builder) => {

    }
});

export const settingsSelectors = {
    getDirection: createSelector(
        (state) => {
            return state.settings.dir;
        },
        (dir) => dir,
    ),
    getTheme: createSelector(
        (state) => {
            return state.settings.theme;
        },
        (theme) => theme,
    ),
    getFontSize: createSelector(
        (state) => {
            return state.settings.fontSize;
        },
        (fontSize) => fontSize,
    ),
    getSidebarStatus: createSelector(
        (state) => {
            return state.settings.sidebarOpen;
        },
        (sidebarOpen) => sidebarOpen,
    ),
    getWebContext: createSelector(
        (state) => {
            return state.settings.webContext;
        },
        (webContext) => webContext,
    ),
    getUserContext: createSelector(
        (state) => {
            return state.settings.userContext;
        },
        (userContext) => userContext,
    ),
    getLayout: createSelector(
        (state) => {
            return state.settings.layout;
        },
        (layout) => layout,
    ),
    getGlobalLoading: createSelector(
        (state) => {
            return state.settings.globalLoading;
        },
        (globalLoading) => globalLoading,
    ),
    getSidebarItems: createSelector(
        (state) => {
            return state.settings.sidebarItems;
        },
        (sidebarItems) => sidebarItems,
    ),
    getSiteImages: createSelector(
        (state) => {
            return state.settings.siteImages;
        },
        (siteImages) => siteImages,
    )
}

const {reducer, actions} = settingsSlice;
export const {
    updateAppTheme,
    updateWebContext,
    updateFontSize,
    updateAppDirection,
    updateSidebarStatus,
    updateAppLanguage,
    updateLayout,
    updateSidebarItems,
    updateGlobalLoading,
    updateSiteImages
} = actions;
export default reducer;